'use client';

import _extends from "@babel/runtime/helpers/esm/extends";
import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["slots", "slotProps", "suggestions", "autoSubmit", "alwaysVisible", "children"];
import * as React from 'react';
import useSlotProps from '@mui/utils/useSlotProps';
import { useMessageIds } from "../hooks/useMessage.js";
import { useChatComposer } from "../hooks/useChatComposer.js";
import { useChatLocaleText } from "../chat/internals/ChatLocaleContext.js";
import { getDataAttributes } from "../internals/getDataAttributes.js";
import { SuggestionsContext } from "./internals/SuggestionsContext.js";
import { SuggestionItem } from "./SuggestionItem.js";
import { jsx as _jsx } from "react/jsx-runtime";
function normalizeSuggestion(item) {
  return typeof item === 'string' ? {
    value: item
  } : item;
}
export const SuggestionsRoot = /*#__PURE__*/React.forwardRef(function SuggestionsRoot(props, ref) {
  const {
      slots,
      slotProps,
      suggestions,
      autoSubmit = false,
      alwaysVisible = false,
      children
    } = props,
    other = _objectWithoutPropertiesLoose(props, _excluded);
  const messageIds = useMessageIds();
  const {
    setValue,
    submit
  } = useChatComposer();
  const localeText = useChatLocaleText();
  const isEmpty = messageIds.length === 0;
  const normalized = React.useMemo(() => (suggestions ?? []).map(normalizeSuggestion), [suggestions]);
  const suggestionCount = children ? React.Children.count(children) : normalized.length;
  const ownerState = React.useMemo(() => ({
    isEmpty,
    suggestionCount
  }), [isEmpty, suggestionCount]);
  const onSelect = React.useCallback(value => {
    setValue(value);
    if (autoSubmit) {
      // Defer submit so the store has the new value before validation.
      void Promise.resolve().then(() => submit());
    }
  }, [autoSubmit, setValue, submit]);
  const contextValue = React.useMemo(() => ({
    onSelect
  }), [onSelect]);
  const Root = slots?.root ?? 'div';
  const rootProps = useSlotProps({
    elementType: Root,
    externalSlotProps: slotProps?.root,
    externalForwardedProps: other,
    ownerState,
    additionalProps: _extends({
      ref,
      role: 'group',
      'aria-label': localeText.suggestionsLabel
    }, getDataAttributes({
      empty: isEmpty,
      count: suggestionCount
    }))
  });
  if (!isEmpty && !alwaysVisible) {
    return null;
  }
  const itemSlots = slots?.item ? {
    root: slots.item
  } : undefined;
  return /*#__PURE__*/_jsx(SuggestionsContext.Provider, {
    value: contextValue,
    children: /*#__PURE__*/_jsx(Root, _extends({}, rootProps, {
      children: children ?? normalized.map((suggestion, index) => /*#__PURE__*/_jsx(SuggestionItem, {
        value: suggestion.value,
        label: suggestion.label,
        index: index,
        slots: itemSlots,
        slotProps: {
          root: slotProps?.item
        }
      }, suggestion.value))
    }))
  });
});
if (process.env.NODE_ENV !== "production") SuggestionsRoot.displayName = "SuggestionsRoot";